import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { blocked, completed, failed } from 'flare-chat-core/contracts/chat/operation-result';
import { createSubmissionState } from './submission-state';

const ACCEPTED_STATUSES = new Set(['admitted', 'already_applied']);
const identityKey = (sessionId, runId, input) => JSON.stringify([sessionId, runId,
  Object.entries(input).filter(([key]) => !['idempotency_key', 'submitted_at'].includes(key))
    .sort(([left], [right]) => left.localeCompare(right)),
]);

/** @param {import('./contracts').SubmissionOptions} options */
export function useActionInputSubmission({
  activeSessionId, runAPI, onRefreshSession, onRefreshSessions,
} = {}) {
  const [state, setState] = useState({ owner: null, pending: false, error: null });
  const submissions = useRef(new Map());
  const scope = useMemo(() => {
    const previous = submissions.current.get(activeSessionId);
    const receipt = previous?.runAPI === runAPI ? previous : { runAPI, owner: createSubmissionState() };
    submissions.current.set(activeSessionId, receipt);
    return { active: true, activeSessionId, runAPI, owner: receipt.owner };
  }, [activeSessionId, runAPI]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  useEffect(() => {
    scope.active = true;
    return () => { scope.active = false; };
  }, [scope]);

  const submit = useCallback(async ({ actionInputSubmission, runId } = {}) => {
    const isCurrent = () => scope.active && currentScope.current === scope;
    if (!isCurrent() || typeof runAPI?.submitActionInput !== 'function') return blocked('action_input_unavailable');
    if (!actionInputSubmission || !runId) return blocked('action_input_invalid');
    const markPending = () => setState({ owner: scope, pending: true, error: null });
    const result = await scope.owner.execute({
      key: identityKey(activeSessionId, runId, actionInputSubmission),
      request: actionInputSubmission, operation: 'submitActionInput', isCurrent,
      submit: async (request) => {
        markPending();
        const response = await runAPI.submitActionInput(runId, request);
        if (response?.run_id !== runId) return failed(Object.assign(new Error('输入回执不匹配。'), {
          code: 'invalid_kernel_run_identity', response,
        }), 'submitActionInput');
        return ACCEPTED_STATUSES.has(response?.status) ? completed(response)
          : failed(Object.assign(new Error('本次输入未被接受，请刷新后重试。'), {
            code: response?.reason || 'action_input_rejected', response,
          }), 'submitActionInput');
      },
      refresh: async () => {
        markPending();
        await Promise.all([onRefreshSession?.(), onRefreshSessions?.()]);
      },
    });
    if (!isCurrent() || result.reason === 'submission_pending') return result;
    setState({ owner: scope, pending: false, error: result.error ?? null });
    return result;
  }, [activeSessionId, onRefreshSession, onRefreshSessions, runAPI, scope]);

  return { error: state.owner === scope ? state.error : null, pending: state.owner === scope && state.pending, submit };
}
export default useActionInputSubmission;
